// CloseWorkspaceModal - workspace close confirmation modal
// Implements US-2.4: Close Workspace with Export Status Check

import React, { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import styles from './CloseWorkspaceModal.module.css';

interface CloseWorkspaceModalProps {
  isOpen: boolean;
  workspaceName: string;
  hasBeenExported: boolean;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

/**
 * RENDER close workspace confirmation modal
 * - Shows different warning based on export status
 * - Prevents accidental data loss
 * - Cancel/Confirm actions
 * - Loading state during close operation
 */
export const CloseWorkspaceModal: React.FC<CloseWorkspaceModalProps> = ({
  isOpen,
  workspaceName,
  hasBeenExported,
  onConfirm,
  onCancel,
}) => {
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * HANDLE escape key (close modal)
   */
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isClosing) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return;
  }, [isOpen, isClosing, onCancel]);

  /**
   * HANDLE backdrop click (close modal)
   */
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isClosing) {
      onCancel();
    }
  };

  /**
   * HANDLE confirm button click
   */
  const handleConfirm = async () => {
    setIsClosing(true);
    setError(null);

    try {
      await onConfirm();
      // Success! Modal will be closed by parent component or navigation
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Workspace konnte nicht geschlossen werden');
      setIsClosing(false);
    }
  };

  /**
   * HANDLE cancel button
   */
  const handleCancel = () => {
    if (!isClosing) {
      setError(null);
      onCancel();
    }
  };

  // EARLY return after all hooks
  if (!isOpen) return null;

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal}>
        {/* HEADER */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <AlertTriangle size={24} strokeWidth={2} />
          </div>
          <h3>Workspace schließen</h3>
        </div>

        {/* CONTENT */}
        <div className={styles.content}>
          <p className={styles.workspaceName}>
            <strong>{workspaceName}</strong>
          </p>

          {/* CONDITIONAL WARNING MESSAGE */}
          {!hasBeenExported ? (
            <div className={styles.warningBox}>
              <p className={styles.warningText}>
                <strong>Dieser Workspace wurde noch nicht exportiert.</strong>
              </p>
              <p className={styles.warningSubtext}>
                Sobald geschlossen, wird der Workspace schreibgeschützt. Du kannst weiterhin auf Exports zugreifen, wenn du vor dem Schließen exportierst.
              </p>
            </div>
          ) : (
            <p className={styles.infoText}>
              Workspace schließen? Du kannst weiterhin auf Exports von der Workspace-Liste zugreifen.
            </p>
          )}

          <div className={styles.infoBox}>
            <p>
              <strong>Was passiert beim Schließen:</strong>
            </p>
            <ul>
              <li>Der Workspace wird schreibgeschützt für alle Teilnehmer:innen</li>
              <li>Es können keine weiteren Änderungen vorgenommen werden</li>
              <li>Exports bleiben zum Download verfügbar</li>
              <li>Geschlossene Workspaces können nicht wieder geöffnet werden</li>
            </ul>
          </div>

          {/* ERROR MESSAGE */}
          {error && <div className={styles.error}>{error}</div>}
        </div>

        {/* ACTIONS */}
        <div className={styles.actions}>
          <button className={styles.cancelButton} onClick={handleCancel} disabled={isClosing}>
            Abbrechen
          </button>
          <button
            className={styles.closeButton}
            onClick={handleConfirm}
            disabled={isClosing}
          >
            {isClosing ? 'Schließe...' : 'Workspace schließen'}
          </button>
        </div>
      </div>
    </div>
  );
};
